// modules/person-events/shared/validators/event-type-registry.ts
import { consola } from 'consola';
import { z } from 'zod';
import type { ZodSchema } from 'zod';

export interface EventTypeDefinition<T extends string = string> {
    type: T;
    schema: ZodSchema;
    metadataSchema: ZodSchema;
}

class PersonEventTypeRegistry {
    private eventTypes = new Map<string, EventTypeDefinition>();

    /**
     * Register a new event type with its validation schema
     */
    register<T extends string>(
        definition: EventTypeDefinition<T>
    ): void {
        if (this.eventTypes.has(definition.type)) {
            consola.warn(
                `[EVENT REGISTRY] Event type "${definition.type}" is already registered. Overwriting...`
            );
        }
        this.eventTypes.set(definition.type, definition);
        consola.info(`[EVENT REGISTRY] Registered event type: ${definition.type}`);
    }

    /**
     * Get validation schema for a specific event type
     */
    getSchema(eventType: string): ZodSchema | undefined {
        return this.eventTypes.get(eventType)?.schema;
    }

    /**
     * Get metadata schema for a specific event type
     */
    getMetadataSchema(eventType: string): ZodSchema | undefined {
        return this.eventTypes.get(eventType)?.metadataSchema;
    }

    /**
     * Get all registered event types
     */
    getRegisteredTypes(): string[] {
        return Array.from(this.eventTypes.keys());
    }

    /**
     * Validate event data for a specific type
     */
    validate<T = unknown>(eventType: string, data: unknown): {
        success: boolean;
        data?: T;
        error?: z.ZodError;
    } {
        const schema = this.getSchema(eventType);
        if (!schema) {
            return {
                success: false,
                error: new z.ZodError([
                    {
                        code: 'custom',
                        path: ['type'],
                        message: `Unknown event type: ${eventType}`,
                    },
                ]),
            };
        }

        const result = schema.safeParse(data);
        if (result.success) {
            return { success: true, data: result.data as T };
        }
        return { success: false, error: result.error };
    }
}

// Singleton instance
export const personEventTypeRegistry = new PersonEventTypeRegistry();